import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, FileText, CheckCircle, AlertCircle, PenTool, Calendar, Search } from "lucide-react";
import api from "../utils/api";
import { toSydneyDate } from "../utils/helpers";
import { useAuth } from "../context/AuthContext";

interface MemosAndPoliciesTabProps {
    userId: string;
}

interface AssignmentItem {
    id: string;
    type: "policy" | "memo";
    title: string;
    status: string;
    requireSignature: boolean;
    assignedDate: string;
    readDate: string | null;
    signedDate: string | null;
    effectiveDate: string | null;
    version: string | number;
    documentId: string;
}

export const MemosAndPoliciesTab: React.FC<MemosAndPoliciesTabProps> = ({ userId }) => {
    const [items, setItems] = useState<AssignmentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const { currentUser } = useAuth();

    useEffect(() => {
        const fetchAssignments = async () => {
            try {
                setLoading(true);
                setError(null);

                const isOwnProfile = currentUser?.id === userId;
                const policyUrl = isOwnProfile ? "/api/policies/user/my-policies" : `/api/policies/user/${userId}/policies`;
                const memoUrl = isOwnProfile ? "/api/memos/user/my-memos" : `/api/memos/user/${userId}/memos`;

                const [policyData, memoData] = await Promise.all([
                    api.get(policyUrl),
                    api.get(memoUrl)
                ]);

                const combinedItems: AssignmentItem[] = [];

                if (policyData.success && policyData.assignments) {
                    policyData.assignments.forEach((assignment: any) => {
                        if (assignment.policy && !assignment.policy.deleted) {
                            combinedItems.push({
                                id: assignment.id,
                                type: "policy",
                                title: assignment.policy.title || "Untitled Policy",
                                status: assignment.status || "Assigned",
                                requireSignature: assignment.policy.requireSignature,
                                assignedDate: assignment.assignedDate,
                                readDate: assignment.readDate,
                                signedDate: assignment.signedDate,
                                effectiveDate: assignment.policy.effectiveDate,
                                version: assignment.policyVersion || assignment.policy.version || 1,
                                documentId: assignment.policy.id,
                            });
                        }
                    });
                }

                if (memoData.success && memoData.assignments) {
                    memoData.assignments.forEach((assignment: any) => {
                        if (assignment.memo && !assignment.memo.deleted) {
                            combinedItems.push({
                                id: assignment.id,
                                type: "memo",
                                title: assignment.memo.subject || "Untitled Memo",
                                status: assignment.status || "Assigned",
                                requireSignature: assignment.memo.requireSignature,
                                assignedDate: assignment.assignedDate,
                                readDate: assignment.readDate,
                                signedDate: assignment.signedDate,
                                effectiveDate: assignment.memo.effectiveDate,
                                version: assignment.memoVersion || assignment.memo.version || 1,
                                documentId: assignment.memo.id,
                            });
                        }
                    });
                }

                combinedItems.sort((a, b) =>
                    new Date(b.assignedDate).getTime() - new Date(a.assignedDate).getTime()
                );

                setItems(combinedItems);
            } catch (err) {
                console.error("Error fetching memos and policies:", err);
                setError("Failed to load memos and policies data.");
            } finally {
                setLoading(false);
            }
        };

        fetchAssignments();
    }, [userId]);

    const filteredItems = useMemo(() => {
        return items.filter((item) => {
            const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = typeFilter === "all" || item.type === typeFilter;
            return matchesSearch && matchesType;
        });
    }, [items, searchTerm, typeFilter]);

    const formatDate = (dateString: string | null) => {
        return toSydneyDate(dateString);
    };

    const getStatusInfo = (item: AssignmentItem) => {
        if (item.requireSignature) {
            if (item.signedDate) {
                return {
                    label: "Signed",
                    icon: <CheckCircle size={14} className="text-green-500" />,
                    badgeClass: "bg-green-100 text-green-700 border-green-200",
                };
            }
            if (item.readDate) {
                return {
                    label: "Pending Signature",
                    icon: <PenTool size={14} className="text-orange-500" />,
                    badgeClass: "bg-orange-100 text-orange-700 border-orange-200",
                };
            }
            return {
                label: "Unread",
                icon: <AlertCircle size={14} className="text-red-500" />,
                badgeClass: "bg-red-100 text-red-700 border-red-200",
            };
        }

        if (item.status === "Completed" || item.readDate) {
            return {
                label: "Read",
                icon: <CheckCircle size={14} className="text-green-500" />,
                badgeClass: "bg-green-100 text-green-700 border-green-200",
            };
        }
        return {
            label: "Unread",
            icon: <AlertCircle size={14} className="text-red-500" />,
            badgeClass: "bg-red-100 text-red-700 border-red-200",
        };
    };

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return <div className="text-red-500 p-4">{error}</div>;
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search documents..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full sm:w-[160px]">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="policy">Policies</SelectItem>
                        <SelectItem value="memo">Memos</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
                {/* Mobile View */}
                <div className="md:hidden space-y-0 divide-y">
                    {filteredItems.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
                            <p>No policies or memos found.</p>
                        </div>
                    ) : (
                        filteredItems.map((item) => {
                            const statusInfo = getStatusInfo(item);
                            return (
                                <div key={item.id} className="p-4 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <Link
                                                to={item.type === "policy" ? `/my-policies/${item.documentId}/view` : `/memo-sign/${item.documentId}`}
                                                className="font-medium text-sm text-primary hover:underline block truncate"
                                            >
                                                {item.title}
                                            </Link>
                                        </div>
                                        <Badge variant="outline" className={`${statusInfo.badgeClass} text-[10px] flex items-center gap-1 whitespace-nowrap`}>
                                            {statusInfo.icon}
                                            {statusInfo.label}
                                        </Badge>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge variant="outline" className="text-[10px]">
                                            {item.type === "policy" ? "Policy" : "Memo"}
                                        </Badge>
                                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                            v{item.version}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        {item.signedDate && <span>Signed: {formatDate(item.signedDate)}</span>}
                                        {!item.signedDate && item.readDate && <span>Read: {formatDate(item.readDate)}</span>}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[40%]">Document</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Signed / Read</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                        No policies or memos found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredItems.map((item) => {
                                    const statusInfo = getStatusInfo(item);
                                    return (
                                        <TableRow key={item.id} className="hover:bg-slate-50">
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <FileText size={16} className="text-muted-foreground flex-shrink-0" />
                                                    <div>
                                                        <Link
                                                            to={item.type === "policy" ? `/my-policies/${item.documentId}/view` : `/memo-sign/${item.documentId}`}
                                                            className="font-medium text-sm hover:underline text-primary"
                                                        >
                                                            {item.title}
                                                        </Link>
                                                        <div className="text-xs text-muted-foreground">
                                                            v{item.version}
                                                            {item.effectiveDate && (
                                                                <span className="ml-2">
                                                                    <Calendar size={10} className="inline mr-1" />
                                                                    Effective: {formatDate(item.effectiveDate)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-[10px]">
                                                    {item.type === "policy" ? "Policy" : "Memo"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`${statusInfo.badgeClass} text-[10px] flex items-center gap-1 w-fit`}>
                                                    {statusInfo.icon}
                                                    {statusInfo.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-xs text-muted-foreground">
                                                    {item.signedDate
                                                        ? formatDate(item.signedDate)
                                                        : item.readDate
                                                            ? formatDate(item.readDate)
                                                            : "-"}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};
